import CountUp from "react-countup"
import {
  Flame,
  PhoneCall,
  TrendingUp,
  Moon,
  AlertTriangle,
  Inbox,
  CheckCircle2
} from "lucide-react"
import { cn } from "../../utils/classnames"
import { LeadScoreCategory, LeadScoreStats } from "../../../types"
import { CATEGORY_CONFIG, CATEGORIES } from "./category-helpers"

type AcknowledgedFilter = "new" | "reviewed" | "all"

interface Props {
  stats: LeadScoreStats | undefined
  activeCategory: LeadScoreCategory | null
  onCategoryClick: (category: LeadScoreCategory | null) => void
  acknowledgedFilter: AcknowledgedFilter
  onNeedsReviewClick: () => void
}

const CATEGORY_ICONS: Record<LeadScoreCategory, React.ElementType> = {
  hot_lead: Flame,
  needs_contact: PhoneCall,
  engaged: TrendingUp,
  dormant: Moon,
  at_risk: AlertTriangle
}

const ScoreSummaryCards = ({
  stats,
  activeCategory,
  onCategoryClick,
  acknowledgedFilter,
  onNeedsReviewClick
}: Props) => {
  const unacknowledged = stats?.unacknowledged ?? 0
  const isNeedsReviewActive =
    acknowledgedFilter === "new" && activeCategory === null

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {/* Needs Review card */}
      <button
        onClick={onNeedsReviewClick}
        className={cn(
          "rounded-lg border-t-2 border border-primary-200 p-4 text-left transition-all",
          "hover:shadow-md cursor-pointer",
          "border-t-primary-400 bg-primary-50",
          isNeedsReviewActive && "ring-2 ring-primary-400 shadow-md"
        )}
      >
        <div className="flex items-center gap-2 mb-2">
          {unacknowledged > 0 ? (
            <Inbox size={16} className="text-primary-500" />
          ) : (
            <CheckCircle2 size={16} className="text-green-500" />
          )}
          <span className="text-xs font-medium text-primary-500 uppercase tracking-wide">
            {unacknowledged > 0 ? "Needs Review" : "All Clear"}
          </span>
        </div>
        <div className="text-2xl font-bold text-primary-600">
          <CountUp end={unacknowledged} duration={0.8} />
        </div>
        {unacknowledged > 0 && stats?.total != null && (
          <div className="text-xs text-primary-400 mt-1">
            of {stats.total} total
          </div>
        )}
      </button>

      {/* Category cards */}
      {CATEGORIES.map((category, i) => {
        const config = CATEGORY_CONFIG[category]
        const Icon = CATEGORY_ICONS[category]
        const count = stats?.[category] ?? 0
        const isActive = activeCategory === category

        return (
          <button
            key={category}
            onClick={() => onCategoryClick(isActive ? null : category)}
            className={cn(
              "bg-white rounded-lg border-t-2 border border-neutral-200 p-4 text-left transition-all",
              "hover:shadow-md cursor-pointer",
              config.cardAccentClass,
              isActive && "ring-2 ring-primary-400 shadow-md"
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon size={16} className="text-neutral-500" />
              <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                {config.labelPlural}
              </span>
            </div>
            <div className="text-2xl font-bold text-neutral-800">
              <CountUp end={count} duration={0.8} delay={(i + 1) * 0.15} />
            </div>
            {count > 0 && stats?.avg_score != null && !activeCategory && (
              <div className="text-xs text-neutral-400 mt-1">
                {count} of {stats.total}
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

export default ScoreSummaryCards
